import React from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import {
  Download as DownloadIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface DocumentViewerProps {
  htmlContent?: string | null;
  fileUrl?: string | null;
  fileType?: string | null;
  title: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  htmlContent,
  fileUrl,
  fileType,
  title,
}) => {
  const { t } = useTranslation();

  const isPdf = fileType?.toLowerCase() === 'pdf' || fileUrl?.toLowerCase().endsWith('.pdf');

  return (
    <>
      {/* Inline HTML document view - styled like a real paper document */}
      {htmlContent && (
        <Card
          sx={{
            mb: 2,
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              bgcolor: 'grey.100',
              p: { xs: 1.5, sm: 3 },
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                bgcolor: 'white',
                width: '100%',
                maxWidth: 720,
                minHeight: 200,
                p: { xs: 2.5, sm: 4, md: 5 },
                boxShadow: '0 1px 4px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
                borderRadius: 0.5,
                overflow: 'auto',
                '& *': {
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                },
                '& img': {
                  height: 'auto',
                },
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  fontSize: '0.9rem',
                },
                '& th, & td': {
                  border: '1px solid #ddd',
                  p: 1,
                  textAlign: 'left',
                },
              }}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </Box>
        </Card>
      )}

      {/* File download / view section */}
      {fileUrl && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            {/* For PDFs on desktop, show embedded preview */}
            {isPdf && (
              <Box
                sx={{
                  mb: 2,
                  display: { xs: 'none', sm: 'block' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="iframe"
                  src={fileUrl}
                  sx={{
                    width: '100%',
                    height: 500,
                    border: 'none',
                    display: 'block',
                  }}
                  title={title}
                />
              </Box>
            )}

            <Button
              variant="outlined"
              startIcon={isPdf ? <DocumentIcon /> : <DownloadIcon />}
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
              size="large"
              sx={{ textTransform: 'none', py: 1.5 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="body1" fontWeight={500}>
                  {t('document.downloadDocument')}
                </Typography>
                {fileType && (
                  <Typography variant="caption" color="text.secondary">
                    {fileType.toUpperCase()}
                  </Typography>
                )}
              </Box>
            </Button>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default DocumentViewer;
